import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DriverData } from "../../vendor/vehicleAdmin/vehicleAuditComponents/types";

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  position: "relative",
  cursor: "pointer",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
  },
}));

interface ExecutionInfoCardProps {
  driver: DriverData;
  onImageClick?: (images: string[], startIndex: number) => void;
  jobType?: string;
  style?: React.CSSProperties;
  showImages: boolean;
}

export const ExecutionInfoCard: React.FC<ExecutionInfoCardProps> = ({
  driver,
  onImageClick,
  jobType,
  style,
  showImages = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const images = driver.Images || [];

  const [loaded, setLoaded] = useState<boolean[]>(
    Array(images.slice(0, 3).length).fill(false)
  );

  const allLoaded = loaded.every(Boolean) || !showImages;

  const handleImageLoad = (index: number) => {
    setLoaded((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  const handleImageClick = (index: number) => {
    if (onImageClick && allLoaded) {
      onImageClick(images, index);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        "&:hover": { boxShadow: theme.shadows[4] },
        mx: isMobile ? 0.5 : 1,
        my: isMobile ? 0.5 : 1,
        minWidth: isMobile ? 280 : undefined,
      }}
      style={style}
    >
      <CardContent
        sx={{
          p: isMobile ? 1.5 : 2.5,
          "&:last-child": { pb: isMobile ? 1.5 : 2.5 },
        }}
      >
        {/* Driver Details */}
        {jobType === "auto_branding" && (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 900 }}
                color="text.secondary"
              >
                {driver?.["Driver Details: "]?.name || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  RC Number:
                </Box>{" "}
                {driver?.["Driver Details: "]?.rcNumber?.toUpperCase() || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Phone Number:
                </Box>{" "}
                {driver?.["Driver Details: "]?.phoneNumber || "N/A"}
              </Typography>
            </Box>
          </>
        )}

        <Box display="flex" flexDirection="column">
          <Box>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Installer:
              </Box>{" "}
              {driver.installerName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Date:
              </Box>{" "}
              {driver.date ? new Date(driver.date).toLocaleString() : "N/A"}
            </Typography>
          </Box>
          {Boolean(
            driver["Installation Location"]?.latitude &&
              driver["Installation Location"]?.longitude
          ) && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Location:
                </Box>
                {` ${driver["Installation Location"].latitude}, ${driver["Installation Location"].longitude}`}
              </Typography>
            </Box>
          )}

          {showImages && images.length > 0 && (
            <Box mt={0}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? "0.875rem" : "0.9375rem",
                }}
              >
                Installation Photos:
              </Typography>

              <Box
                display="flex"
                gap={isMobile ? 0.5 : 1}
                flexWrap="nowrap"
                overflow="auto"
                py={0.5}
                sx={{
                  "&::-webkit-scrollbar": { height: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.grey[400],
                    borderRadius: "2px",
                  },
                }}
              >
                {images.slice(0, 3).map((img, index) => (
                  <SmallAvatar
                    key={`${driver.taskId}-${index}`}
                    variant="square"
                    onClick={() => handleImageClick(index)}
                    sx={{
                      cursor: allLoaded ? "pointer" : "default",
                      opacity: allLoaded ? 1 : 0.6,
                    }}
                  >
                    {!loaded[index] && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.6)",
                          borderRadius: 1,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    <img
                      src={img}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 4,
                        display: loaded[index] ? "block" : "none",
                      }}
                      onLoad={() => handleImageLoad(index)}
                    />
                  </SmallAvatar>
                ))}

                {images.length > 3 && (
                  <SmallAvatar
                    sx={{
                      backgroundColor: "grey.200",
                      fontSize: isMobile ? "0.7rem" : "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: isMobile ? 40 : 56,
                    }}
                  >
                    +{images.length - 3}
                  </SmallAvatar>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
